import { apiGet, apiPost, apiPatch } from './api';
import { API_URL } from './authServices';

/** PUT helper (não tem em api.ts ainda) — exportado pra outros services */
export async function apiPut<T = any>(path: string, body: unknown): Promise<T> {
  const raw = localStorage.getItem('liberty_auth');
  let token = '';
  try { token = raw ? JSON.parse(raw).token : ''; } catch { /* ignore */ }
  const res = await fetch(`${API_URL}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Erro' }));
    throw err;
  }
  return res.json();
}

async function apiDelete<T = any>(path: string): Promise<T> {
  const raw = localStorage.getItem('liberty_auth');
  let token = '';
  try { token = raw ? JSON.parse(raw).token : ''; } catch { /* ignore */ }
  const res = await fetch(`${API_URL}${path}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Erro' }));
    throw err;
  }
  return res.json();
}

/* ───────────────────────────── Dashboard stats ─────────────────────────────── */

export interface DashboardStats {
  onlineAccounts: number;
  onlineChars: number;
  onlineDungeon: number;    // chars in-game num WorldIdx de dungeon
  onlineWar: number;        // chars in-game no World 15 (guerra)
  onlineGuard: number;      // legado — substituido por loginsToday no UI
  loginsToday: number;       // contas distintas que logaram hoje (_39_AntiCheatHeader.UltimoLogin)
  createdToday: number;
  guardWindowSeconds: number;
  note?: string;
}

export const getDashboardStats = (): Promise<DashboardStats> =>
  apiGet<DashboardStats>('/admin/dashboard/stats');

export interface TopCharacter {
  characterIdx: number;
  userNum?: number | null;
  name?: string | null;
  lev?: number | null;
  charClass?: number | null;
  nation?: number | null;
  alz?: number | null;
  ov?: number | null;
  power?: number | null;
  pveAttack?: number | null;
  pveDefense?: number | null;
  guildName?: string | null;
}
// Conta no ranking de mais ricos (soma de chars + warehouse por conta).
export interface TopAccount {
  userNum: number;
  totalAlz: number;         // chars + warehouse
  charsAlz: number;         // soma do Alz de todos os chars
  warehouseAlz: number;     // Alz do armazem
  name?: string | null;     // nome do char com maior PlayTime (rosto da conta)
  characterIdx?: number | null;
  nation?: number | null;
  lev?: number | null;
  charClass?: number | null;
  charCount?: number | null;
}
export const getTopLevel = () => apiGet<TopCharacter[]>('/admin/dashboard/topLevel');
export const getTopRichest = () => apiGet<TopAccount[]>('/admin/dashboard/topRichest');
export const getTopPower = () => apiGet<TopCharacter[]>('/admin/dashboard/topPower');

export interface NationBalance {
  procyon: number;
  capella: number;
  neutral: number;
}
export const getNationBalance = () => apiGet<NationBalance>('/admin/dashboard/nationBalance');

export interface ClassCount {
  classId: number;
  count: number;
}
export const getTopClass = () => apiGet<ClassCount[]>('/admin/dashboard/topClass');

/* ─────────────────────────── Blacklist (DLL/dir patterns) ─────────────────── */
export interface BlacklistRow {
  id: number;
  pattern: string;
  kind: number;          // 1=dll/file, 2=dir, 3=exe
  enabled: boolean;
  notes?: string | null;
  createdDate?: string | null;
}
export interface BlacklistInput {
  pattern: string;
  kind: number;
  enabled?: boolean;
  notes?: string | null;
}
export const listBlacklist = (params?: { enabled?: boolean; kind?: number; search?: string }) => {
  const qs = new URLSearchParams();
  if (params?.enabled !== undefined) qs.append('enabled', String(params.enabled));
  if (params?.kind !== undefined) qs.append('kind', String(params.kind));
  if (params?.search) qs.append('search', params.search);
  const q = qs.toString();
  return apiGet<BlacklistRow[]>(`/admin/dllBlacklist${q ? '?' + q : ''}`);
};
export const createBlacklist = (input: BlacklistInput) =>
  apiPost<BlacklistRow>('/admin/dllBlacklist', input);
export const updateBlacklist = (id: number, input: BlacklistInput) =>
  apiPut<BlacklistRow>(`/admin/dllBlacklist/${id}`, input);
export const deleteBlacklist = (id: number) =>
  apiDelete<{ status: string; message: string }>(`/admin/dllBlacklist/${id}`);

export interface AlzTotal {
  alzInventory: number;
  alzWarehouse: number;
  alzMail: number;
  alzGuild: number;
  alzTotal: number;
}
export const getAlzTotal = () => apiGet<AlzTotal>('/admin/dashboard/alzTotal');

export interface AlzRates {
  perHour: number;
  perDay: number;
  latestTotal: number;
  capturedAt?: string | null;
}
export const getAlzRates = () => apiGet<AlzRates>('/admin/dashboard/alzRates');

export interface CashTotal {
  cashAccounts: number;
  cashInItems: number;
  cashTotal: number;
}
export const getCashTotal = () => apiGet<CashTotal>('/admin/dashboard/cashTotal');

/* ───────────────────────────── Accounts (admin) ───────────────────────────── */

export interface AdminAccountRow {
  userNum: number;
  id?: string | null;
  userName?: string | null;
  email?: string | null;
  authType?: number | null;
  lastIp?: string | null;
  login?: number | null;
  createDate?: string | null;
}

export interface AdminAccountListResponse {
  total: number;
  items: AdminAccountRow[];
}

export type AccountSort = 'user_num' | 'id' | 'user_name' | 'email' | 'auth_type' | 'login' | 'create_date';

export const listAdminAccounts = (params: {
  skip?: number;
  limit?: number;
  search?: string;
  sort?: AccountSort;
  order?: 'asc' | 'desc';
}) => {
  const qs = new URLSearchParams();
  if (params.skip != null) qs.append('skip', String(params.skip));
  if (params.limit != null) qs.append('limit', String(params.limit));
  if (params.search) qs.append('search', params.search);
  if (params.sort) qs.append('sort', params.sort);
  if (params.order) qs.append('order', params.order);
  return apiGet<AdminAccountListResponse>(`/admin/accounts?${qs.toString()}`);
};

/* ───────────────────────────── Characters (admin) ─────────────────────────── */

export interface AdminCharacterRow {
  characterIdx: number;
  userNum: number;
  accountId?: string | null;
  name?: string | null;
  lev?: number | null;
  charClass?: number | null;
  nation?: number | null;
  alz?: number | null;
  login?: number | null;
  playTime?: number | null;
  worldIdx?: number | null;
  channelIdx?: number | null;
  pkPenalty?: number | null;
  ov?: number | null;
}

export interface AdminCharacterListResponse {
  total: number;
  items: AdminCharacterRow[];
}

export type CharacterSort = 'character_idx' | 'name' | 'lev' | 'alz' | 'login' | 'play_time' | 'user_num' | 'ov' | 'char_class' | 'nation';

export const listAdminCharacters = (params: {
  skip?: number;
  limit?: number;
  search?: string;
  sort?: CharacterSort;
  order?: 'asc' | 'desc';
}) => {
  const qs = new URLSearchParams();
  if (params.skip != null) qs.append('skip', String(params.skip));
  if (params.limit != null) qs.append('limit', String(params.limit));
  if (params.search) qs.append('search', params.search);
  if (params.sort) qs.append('sort', params.sort);
  if (params.order) qs.append('order', params.order);
  return apiGet<AdminCharacterListResponse>(`/admin/characters?${qs.toString()}`);
};

/* ───────────────────────────── Panel (search/ban/mail/sendItem) ───────────── */
/* IMPORTANTE: schemas antigos (panel/schemas.py) sao PascalCase no Python e
   o `to_camel` do Pydantic colapsa esses nomes pra LOWERCASE no JSON.
   Por isso `usernum` (nao userNum), `lastip` (nao lastIp), etc. */

export interface CharacterRow {
  characteridx: number;
  name?: string | null;
  lev?: number | null;
  exp?: number | null;
  str?: number | null;
  dex?: number | null;
  int?: number | null;
  pnt?: number | null;
  alz?: number | null;
  style?: number | null;
  nation?: number | null;
  login?: number | null;
  playtime?: number | null;
  worldidx?: number | null;
  channelidx?: number | null;
  class?: number | null;
  ov?: number | null;
  pkpenalty?: number | null;
  guildname?: string | null;
  guildidx?: number | null;
  // F3 — heartbeat liberty.dll nos ultimos 5min (CamelModel colapsa pra lowercase aqui)
  dllhooked?: boolean;
}

export interface AccountFull {
  usernum: number;
  id?: string | null;
  username?: string | null;
  email?: string | null;
  authtype?: number | null;
  lastip?: string | null;
}

export interface CashRow {
  usernum: number;
  cash?: number | null;
  cashbonus?: number | null;
}

export interface WarehouseRow {
  usernum: number;
  alz?: number | null;
}

export interface BannedItem {
  type: string;
  releasedate?: string | null;
  reason: string;
}

export interface AccountPanelView {
  acc: AccountFull;
  chars: CharacterRow[];
  cash?: CashRow | null;
  warehouse?: WarehouseRow | null;
  banneds: BannedItem[];
  inMaintenance?: boolean;   // conta com senha em manutenção (só vem no detalhe)
}

export const panelSearch = (by: string) =>
  apiGet<AccountPanelView[]>(`/Panel/search?by=${encodeURIComponent(by)}`);

export const getAdminAccount = (userNum: number) =>
  apiGet<AccountPanelView>(`/admin/account/${userNum}`);

export interface EditAccountInput {
  cash?: number;
  cashBonus?: number;
  addCash?: number;
  addCashBonus?: number;
}
export const editAdminAccount = (userNum: number, input: EditAccountInput) =>
  apiPost<{ status: string; message: string }>(`/admin/account/${userNum}/edit`, input);

/* ForceGem (por conta) — delta positivo adiciona, negativo remove */
export interface ForceGemInput {
  amount: number;
}
export const editAccountForceGem = (userNum: number, input: ForceGemInput) =>
  apiPost<{ status: string; message: string }>(`/admin/account/${userNum}/forcegem`, input);

/* Full Coleção — aplica o blob fixo de coleção completa */
export const setFullCollection = (userNum: number) =>
  apiPost<{ status: string; message: string }>(`/admin/account/${userNum}/full-collection`, {});

/* Milhas (Mileage Points) — set absoluto */
export interface EditMilhasInput {
  amount: number;
}
export const editAccountMilhas = (userNum: number, input: EditMilhasInput) =>
  apiPost<{ status: string; message: string }>(`/admin/account/${userNum}/milhas`, input);

/* Skill GM — aplica o patch de skills GM (sem body) */
export const applyCharacterSkillGm = (characterIdx: number) =>
  apiPost<{ status: string; message: string }>(`/admin/character/${characterIdx}/skill-gm`, {});

/* Asas (ForceWing) — grade 1-4, level 1-100 */
export interface SetWingsInput {
  grade: number;
  level: number;
}
export const setCharacterWings = (characterIdx: number, input: SetWingsInput) =>
  apiPost<{ status: string; message: string }>(`/admin/character/${characterIdx}/wings`, input);

/* Full Sets (char): Honor/Mith/Runa/Stellar. Honor precisa do userNum. */
export const setFullHonor = (characterIdx: number, userNum: number) =>
  apiPost<{ status: string; message: string }>(`/admin/character/${characterIdx}/full-honor`, { userNum });
export const setFullMith = (characterIdx: number) =>
  apiPost<{ status: string; message: string }>(`/admin/character/${characterIdx}/full-mith`, {});
export const setFullRuna = (characterIdx: number, tipo: 'espada' | 'magica') =>
  apiPost<{ status: string; message: string }>(`/admin/character/${characterIdx}/full-runa`, { tipo });
export const setFullStellar = (characterIdx: number) =>
  apiPost<{ status: string; message: string }>(`/admin/character/${characterIdx}/full-stellar`, {});

/* Full Mérito (conta) */
export const setFullMerito = (userNum: number) =>
  apiPost<{ status: string; message: string }>(`/admin/account/${userNum}/full-merito`, {});

/* ─── Ferramentas GM (dash global) ─── */

/* IPs bloqueados (cabal_blockip_list, banco ACCOUNT) */
export interface BlockedIpRow {
  ip: string;
  blockedBy: string;
  regdate?: string | null;
}
export const listBlockedIps = () => apiGet<BlockedIpRow[]>('/admin/guard/blocked-ips');
export const blockIp = (ip: string) =>
  apiPost<{ status: string; message: string }>('/admin/guard/block-ip', { ip });
export const unblockIp = (ip: string) =>
  apiPost<{ status: string; message: string }>('/admin/guard/unblock-ip', { ip });

/* Guilds (listar) — Server01.Guild */
export interface GuildRow {
  guildNo: number;
  name: string;
  level: number;
  members: number;
}
export const listGuilds = () => apiGet<GuildRow[]>('/admin/guilds');

/* Mail em massa (broadcast) — 2 fases. Alcance = janela por ÚLTIMO LOGIN:
 *   windowMode 'date'  -> quem logou desde `sinceDate` ('YYYY-MM-DD')
 *   'days'|'hours'|'minutes' -> quem logou nas últimas `value` unidades (aceita decimal)
 * Online agora entra sempre. groupByAccount = 1 email por conta (usernum = charIdx/16),
 * pro char de maior uptime (PlayTime). */
export type MailWindowMode = 'date' | 'days' | 'hours' | 'minutes';

export interface MailAudienceInput {
  windowMode: MailWindowMode;
  value?: number;          // days/hours/minutes (decimal ok)
  sinceDate?: string;      // 'YYYY-MM-DD' quando windowMode='date'
  groupByAccount?: boolean;
}

export interface MailPreviewRow {
  charIdx: number;
  name: string;
  userNum: number;
  playTime: number;        // uptime (segundos)
  online: boolean;
}
export interface MailPreviewResult {
  total: number;
  shown: number;           // quantos vieram na amostra (top 500)
  desc: string;
  items: MailPreviewRow[];
}
/* Fase 1 — quem receberia (total + amostra) */
export const mailBroadcastPreview = (input: MailAudienceInput) =>
  apiPost<MailPreviewResult>('/admin/mail/broadcast/preview', input);

/* Fase 2 — dispara pra mesma seleção */
export interface MailBroadcastInput extends MailAudienceInput {
  title: string;
  content: string;
  itemId?: number;
  itemOption?: number;
  itemDuration?: number;
  qty?: number;
  alz?: number;
}
export const mailBroadcast = (input: MailBroadcastInput) =>
  apiPost<{ status: string; message: string }>('/admin/mail/broadcast', input);

/* Criar conta (por GM) — usa a procedure oficial cabal_tool_registerAccount */
export interface CreateAccountInput {
  login: string;
  password: string;
  name: string;
  phone?: string;
}
export const createAccount = (input: CreateAccountInput) =>
  apiPost<{ status: string; message: string }>('/admin/account/create', input);

/* Busca de item por inventário/baú/bike/leilão/guild (Ferramentas). QUERY PESADA. */
export interface ItemSearchOwner {
  src: string;
  location: string;         // "Inventário" | "Baú" | "Bike" | "Leilão" | "Baú Guild" | ...
  ownerType: string;        // char | user | guild | auction
  userNum: number;
  charIdx?: number | null;
  charName?: string | null;
  accountId?: string | null;
  guildNo?: number | null;
  guildName?: string | null;
  qty: number;
  rawKind: number;
  option?: number;              // OptionData (bytes 17-20) de uma cópia representativa
  optionVaries?: boolean;       // true se cópias do mesmo dono têm options diferentes
}
export interface ItemSearchResponse {
  itemId: number;
  cleanId: number;
  itemName?: string | null;
  scope: string;
  ownerCount: number;
  totalQty: number;
  owners: ItemSearchOwner[];
}
export const searchItem = (itemId: number, scope: 'both' | 'inv' | 'wh' = 'both') =>
  apiGet<ItemSearchResponse>('/admin/tools/item-search', { itemId, scope });

/* Log GLOBAL de emails (_39_email_log), paginado + filtros. */
export interface EmailRow {
  logId: number;
  direction: string;          // 'R' recebido | 'S' enviado
  senderCharIdx: number;
  senderName: string;
  receiverCharIdx?: number | null;
  receiverName?: string | null;
  title: string;
  content: string;
  alz: number;
  itemKindIdx: number;
  itemOption: number;
  itemDurationIdx: number;
  logDatetime: string;
}
export interface EmailListResponse {
  total: number;
  items: EmailRow[];
}
export const listEmails = (params: {
  skip?: number; limit?: number; direction?: 'all' | 'R' | 'S'; search?: string;
}) => {
  const q: Record<string, string> = {};
  if (params.skip != null) q.skip = String(params.skip);
  if (params.limit != null) q.limit = String(params.limit);
  if (params.direction) q.direction = params.direction;
  if (params.search) q.search = params.search;
  return apiGet<EmailListResponse>('/admin/emails', q);
};

/* Editar credenciais da conta (login/email-telefone/nome) — campos opcionais */
export interface CredentialsInput {
  login?: string;
  email?: string;
  name?: string;
}
export const editAccountCredentials = (userNum: number, input: CredentialsInput) =>
  apiPost<{ status: string; message: string }>(`/admin/account/${userNum}/credentials`, input);

/* Alterar senha — GM define uma nova senha de login pra conta (porte do Divine) */
export const changeAccountPassword = (userNum: number, password: string) =>
  apiPost<{ status: string; message: string }>(`/admin/account/${userNum}/password`, { password });

/* Manutenção de conta — enable: senha master @Liberty@ / sub 1234 (com backup). disable: restaura */
export const accountMaintenance = (userNum: number, action: 'enable' | 'disable') =>
  apiPost<{ status: string; message: string }>(`/admin/account/${userNum}/maintenance`, { action });

/* Limpeza de personagem (reversível — backup automático em bot_backups) */
export type ClearTipo = 'inventario' | 'equipamento' | 'skilllist' | 'quickslot' | 'tempinv' | 'questdata';
export interface ClearBackupRow { tipo: string; quando: string | null; }
export const getClearStatus = (characterIdx: number) =>
  apiGet<{ backups: ClearBackupRow[] }>(`/admin/character/${characterIdx}/clear/status`);
export const clearCharacter = (characterIdx: number, tipo: ClearTipo) =>
  apiPost<{ status: string; message: string }>(`/admin/character/${characterIdx}/clear/${tipo}`, {});
export const restoreCharacter = (characterIdx: number, tipo: ClearTipo) =>
  apiPost<{ status: string; message: string }>(`/admin/character/${characterIdx}/clear/restore/${tipo}`, {});

/* Zerar UM slot do inventário (diagnóstico — remove item corrompido; backup automático, reversível) */
export const zeroInventorySlot = (characterIdx: number, slot: number, itemId?: number) =>
  apiPost<{ status: string; message: string }>(`/admin/character/${characterIdx}/inventory/zero-slot`, { slot, itemId });
export const undoZeroInventorySlot = (characterIdx: number) =>
  apiPost<{ status: string; message: string }>(`/admin/character/${characterIdx}/inventory/zero-slot/undo`, {});

/* Zerar múltiplos slots (multi-seleção) em inventário / equipamento / armazém.
   id = CharacterIdx (inv/equip) ou UserNum (armazém). Backup automático por source. */
export type ItemZeroSource = 'inventory' | 'equipment' | 'warehouse';
export const zeroItemSlots = (source: ItemZeroSource, id: number, slots: number[], itemIds?: (number | undefined)[]) =>
  apiPost<{ status: string; message: string }>(`/admin/items/zero-slots`, { source, id, slots, itemIds });
export const undoZeroItemSlots = (source: ItemZeroSource, id: number) =>
  apiPost<{ status: string; message: string }>(`/admin/items/zero-slots/undo`, { source, id });

/* Editar/colocar UM item num slot (GM4). Mesmo backup do zerar ("Desfazer último" reverte). Exige offline. */
export interface SetItemSlotBody {
  source: ItemZeroSource; id: number; slot: number; itemId: number;
  option?: number; duration?: number; extreme?: number; divine?: number;
}
export const setItemSlot = (body: SetItemSlotBody) =>
  apiPost<{ status: string; message: string }>(`/admin/items/set-slot`, body);

/* Clonar personagem (origem → destino; destino é sobrescrito e precisa estar offline) */
export interface CloneCharRef { idx: number; name: string; lev: number; }
export interface CloneValidateResult { status: string; src: CloneCharRef; dst: CloneCharRef; }
export const cloneValidate = (srcIdx: number, dstIdx: number) =>
  apiPost<CloneValidateResult>('/admin/character/clone/validate', { srcIdx, dstIdx });
export const cloneExecute = (srcIdx: number, dstIdx: number) =>
  apiPost<{ status: string; message: string }>('/admin/character/clone/execute', { srcIdx, dstIdx });

export const panelOnlines = () => apiGet<CharacterRow[]>('/Panel/onlines');

export interface LogRow {
  idx: number;
  content?: string | null;
  regDateTime?: string | null;
}
export const panelLogs = () => apiGet<LogRow[]>('/Panel/logs');

export interface BanInput {
  userNum: number;
  days: number;
  reason?: string;
}
export const panelBan = (input: BanInput) =>
  apiPost<{ status: string; message: string }>('/Panel/ban', {
    UserNum: input.userNum, Days: input.days, Reason: input.reason,
  });

export const panelUnban = (userId: number) =>
  apiGet<{ status: string; message: string }>(`/Panel/unban?userId=${userId}`);

export interface MailInput { char: number; msg: string; }
export const panelMail = (input: MailInput) =>
  apiPost<{ status: string; message: string }>('/Panel/mail', {
    Char: input.char, Msg: input.msg,
  });

export interface SendItemInput {
  userNum: number;
  itemId: number;
  itemOption?: number;
  itemDuration?: number;
  itemQty?: number;
}
export const panelSendItem = (input: SendItemInput) =>
  apiPost<{ status: string; message: string }>('/Panel/sendItem', {
    UserNum: input.userNum,
    ItemId: input.itemId,
    ItemOption: input.itemOption ?? 0,
    ItemDuration: input.itemDuration ?? 0,
    ItemQty: input.itemQty ?? 1,
  });

export interface BannedView {
  userNum?: number | null;
  id?: string | null;
  releasedate?: string | null;
}
export const panelBanneds = () => apiGet<BannedView[]>('/Panel/banneds');

/* ───────────────────────────── Cash Log (admin) ──────────────────────────── */

export interface CashLogRow {
  cashLogNo: number;
  logType: number;
  logSubType?: number | null;
  cashPre: number;
  cashAfter: number;
  changeCash: number;
  itemCd: number;
  quantity?: number | null;
  tranNo: number;
  logDatetime: string;
  cashBonusPre?: number | null;
  cashBonusAfter?: number | null;
  changeCashBonus?: number | null;
}

export interface CashLogResponse {
  total: number;
  items: CashLogRow[];
}

export const getAccountCashLog = (
  userNum: number,
  skip = 0,
  limit = 50,
) => apiGet<CashLogResponse>(`/admin/account/${userNum}/cashlog?skip=${skip}&limit=${limit}`);

/* ───────────────────────────── Character Detail (admin) ──────────────────── */

export interface CharacterDetailResponse {
  char: CharacterRow;
  userNum: number;
  accountId?: string | null;
}

export const getAdminCharacter = (characterIdx: number) =>
  apiGet<CharacterDetailResponse>(`/admin/character/${characterIdx}`);

export interface EmailLogRow {
  logId: number;
  direction: 'R' | 'S';   // R = recebido, S = enviado
  senderCharIdx: number;
  senderName: string;
  receiverCharIdx?: number | null;
  receiverName?: string | null;
  title: string;
  content: string;
  alz: number;
  itemKindIdx: number;
  itemOption: number;
  itemDurationIdx: number;
  logDatetime: string;
}

export interface EmailLogResponse {
  total: number;
  items: EmailLogRow[];
}

export const getCharacterEmails = (
  characterIdx: number,
  skip = 0,
  limit = 50,
) => apiGet<EmailLogResponse>(`/admin/character/${characterIdx}/emails?skip=${skip}&limit=${limit}`);

/* ───────────────────────────── Edit Character ─────────────────────────────── */

export interface EditCharacterInput {
  lev?: number;
  nation?: number;
  alz?: number;
  addAlz?: number;
  str_?: number;     // backend tem alias 'str_' já em snake_case
  dex?: number;
  int_?: number;
  pnt?: number;
  pkPenalty?: number;
  rank?: number;     // 1-20, honra
  name?: string;     // rename — 2-16 chars, backend checa duplicado
}
export const editCharacter = (characterIdx: number, input: EditCharacterInput) =>
  apiPost<{ status: string; message: string }>(`/admin/character/${characterIdx}/edit`, input);

/* ───────────────────────────── News (admin) ───────────────────────────────── */

export interface AdminNews {
  id: number;
  title?: string | null;
  subTitle?: string | null;
  image?: string | null;
  html?: string | null;
  createdDate?: string | null;
}
export interface AdminNewsInput {
  title: string;
  subTitle?: string;
  image?: string;
  html?: string;
}

export const listAdminNews = () => apiGet<AdminNews[]>('/admin/news');
export const createAdminNews = (input: AdminNewsInput) => apiPost<AdminNews>('/admin/news', input);
export const updateAdminNews = (id: number, input: AdminNewsInput) => apiPut<AdminNews>(`/admin/news/${id}`, input);
export const deleteAdminNews = (id: number) => apiDelete<{ status: string; message: string }>(`/admin/news/${id}`);

/* ───────────────────────────── Recharges (admin) ──────────────────────────── */

export interface AdminRecharge {
  id: number;
  userNum: number;
  accountId?: string | null;
  kitId?: number | null;
  cash?: number | null;
  brl?: number | null;
  status: number;
  date?: string | null;
  gateway?: string | null;            // PIX (EFI) / Mercado Pago / PayPal
  paypalOrder?: string | null;
  mercadoPagoReference?: string | null;
}
export interface AdminRechargeListResponse {
  total: number;
  items: AdminRecharge[];
}
export interface RechargeMetrics {
  soldToday: number;
  soldMonth: number;
  topUserNum?: number | null;
  topAccountId?: string | null;
  topTotal: number;
}
export const getRechargeMetrics = () =>
  apiGet<RechargeMetrics>('/admin/recharges/metrics');
export const listAdminRecharges = (params: { skip?: number; limit?: number; status?: number; user?: number }) => {
  const qs = new URLSearchParams();
  if (params.skip != null) qs.append('skip', String(params.skip));
  if (params.limit != null) qs.append('limit', String(params.limit));
  if (params.status != null) qs.append('status', String(params.status));
  if (params.user != null) qs.append('user', String(params.user));
  return apiGet<AdminRechargeListResponse>(`/admin/recharges?${qs.toString()}`);
};

export const approveRecharge = (id: number) =>
  apiPost<{ status: string; message: string; cashAdded: number }>(`/admin/recharges/${id}/approve`, {});
export const rejectRecharge = (id: number) =>
  apiPost<{ status: string; message: string }>(`/admin/recharges/${id}/reject`, {});

/* ───────────────────────────── Recharge Kits (admin) ─────────────────────── */

export interface RechargeKit {
  id: number;
  title?: string | null;
  cash?: number | null;
  brl?: number | null;
  usd?: number | null;
  image?: string | null;
  active?: number | null;
  bonus?: number | null;   // cash bônus extra creditado junto (0 = sem bônus)
}

export interface RechargeKitInput {
  title?: string;
  cash: number;
  brl?: number;
  usd?: number;
  image?: string;
  active: number;
  bonus?: number;
}

export const listRechargeKits = () => apiGet<RechargeKit[]>('/admin/recharge-kits');
export const createRechargeKit = (input: RechargeKitInput) => apiPost<RechargeKit>('/admin/recharge-kits', input);
export const updateRechargeKit = (id: number, input: RechargeKitInput) => apiPut<RechargeKit>(`/admin/recharge-kits/${id}`, input);
export const deleteRechargeKit = (id: number) => apiDelete<{ status: string; message: string }>(`/admin/recharge-kits/${id}`);

/* ───────────────────────────── Premium Kits (admin) ───────────────────────── */

export interface PremiumKit {
  id: number;
  title?: string | null;
  priceCash?: number | null;
  days?: number | null;
  serviceKind?: number | null;
  discount?: number | null; // % 0-100
}

export interface PremiumKitInput {
  title?: string;
  priceCash: number;
  days: number;
  serviceKind: number;
  discount?: number; // % 0-100
}

export const listPremiumKits = () => apiGet<PremiumKit[]>('/admin/premium-kits');
export const createPremiumKit = (input: PremiumKitInput) => apiPost<PremiumKit>('/admin/premium-kits', input);
export const updatePremiumKit = (id: number, input: PremiumKitInput) => apiPut<PremiumKit>(`/admin/premium-kits/${id}`, input);
export const deletePremiumKit = (id: number) => apiDelete<{ status: string; message: string }>(`/admin/premium-kits/${id}`);

/* ───────────────────────────── Cash Services (admin CRUD) ─────────────────── */

export interface CashServiceRow {
  id: number;
  title?: string;
  subtitle?: string;
  image?: string;
  priceCash?: number;
  active?: number;
  character?: number;  // 1 = serviço de personagem
  account?: number;    // 1 = serviço de conta
  route?: string;      // roda a proc web_cash_service_{route}
}

export interface CashServiceInput {
  title?: string;
  subtitle?: string;
  image?: string;
  priceCash: number;
  active: number;
  character: number;
  account: number;
  route?: string;
}

export const listCashServices = () => apiGet<CashServiceRow[]>('/admin/cash-services');
export const createCashService = (b: CashServiceInput) => apiPost<CashServiceRow>('/admin/cash-services', b);
export const updateCashService = (id: number, b: CashServiceInput) => apiPut<CashServiceRow>(`/admin/cash-services/${id}`, b);
export const deleteCashService = (id: number) => apiDelete<{ status: string; message: string }>(`/admin/cash-services/${id}`);
/** Executa o serviço GRÁTIS pra uma conta (userNum) ou char (characterIdx) — cortesia do GM. */
export const executeCashService = (id: number, target: number) =>
  apiPost<{ status: string; message: string }>(`/admin/cash-services/${id}/execute`, { target });

/* ───────────────────────────── Vendas unificadas (recarga + serviço) ──────── */

export interface SaleRow {
  kind: 'recarga' | 'servico';
  id: number;
  userNum?: number;
  accountId?: string;
  kitId?: number;
  title?: string;
  brl?: number;
  status?: number;
  date?: string;
  gateway?: string;
}

export const listSales = (p: { skip?: number; limit?: number; kind?: string; status?: number }) => {
  const qs = new URLSearchParams();
  if (p.skip != null) qs.append('skip', String(p.skip));
  if (p.limit != null) qs.append('limit', String(p.limit));
  if (p.kind) qs.append('kind', p.kind);
  if (p.status != null) qs.append('status', String(p.status));
  return apiGet<{ items: SaleRow[]; total: number }>(`/admin/sales?${qs.toString()}`);
};

export interface SalesMetrics {
  soldToday: number;
  soldMonth: number;
  topUserNum?: number | null;
  topAccountId?: string | null;
  topTotal: number;
}
/** Cards do Vendas: vendido hoje/mês + maior gastador (recargas + serviços). */
export const getSalesMetrics = () => apiGet<SalesMetrics>('/admin/sales/metrics');

/* ───────────────────────────── Items (inventário/equip/warehouse) ────────── */

export interface ItemRow {
  idx: number;
  itemId: number;
  cleanItemId?: number | null;
  itemName?: string | null;
  slot?: number | null;
  duration?: number | null;
  upgradeLevel?: number | null;
  extremeLevel?: number | null;
  divineLevel?: number | null;
  bindAccount?: number | null;
  bindCharacter?: number | null;
  bindOnEquipped?: number | null;
  isBroken?: number | null;
  isSealed?: number | null;
  slotCount?: number | null;
  slotOptions: Array<string | null>;
  slotForceNames: Array<string | null>;
  craftLevel?: number | null;
  craftOption?: number | null;
  craftName?: string | null;
  optionData?: number | null;
}

export const getInventory = (characterIdx: number) =>
  apiGet<ItemRow[]>(`/admin/inventory/${characterIdx}`);

export const getEquipment = (characterIdx: number) =>
  apiGet<ItemRow[]>(`/admin/equipment/${characterIdx}`);

export const getWarehouse = (userNum: number) =>
  apiGet<ItemRow[]>(`/admin/warehouse/${userNum}`);

/* ───────────────────────────── Premium (admin) ────────────────────────────── */

export interface AdminPremium {
  userNum: number;
  accountId?: string | null;
  type?: number | null;
  serviceKind?: number | null;
  expireDate?: string | null;
  registerDate?: string | null;
}
export interface AdminPremiumListResponse {
  total: number;
  items: AdminPremium[];
}
export const listAdminPremium = (params: { skip?: number; limit?: number; user?: number }) => {
  const qs = new URLSearchParams();
  if (params.skip != null) qs.append('skip', String(params.skip));
  if (params.limit != null) qs.append('limit', String(params.limit));
  if (params.user != null) qs.append('user', String(params.user));
  return apiGet<AdminPremiumListResponse>(`/admin/premium?${qs.toString()}`);
};
export const grantPremium = (userNum: number, days: number, opts?: { type?: number; serviceKind?: number }) =>
  apiPost<{ status: string; message: string }>(`/admin/premium/${userNum}`, {
    days,
    type: opts?.type ?? 1,
    serviceKind: opts?.serviceKind ?? 1,
  });
export const revokePremium = (userNum: number) =>
  apiDelete<{ status: string; message: string; count: number }>(`/admin/premium/${userNum}`);

/* ───────────────────────────── Monitor (GameLog) ──────────────────────────── */

export interface MonitorState {
  online: boolean;
  day?: string;
  alzIn?: number;
  alzOut?: number;
  alzNet?: number;        // alzIn - alzOut (saldo liquido criado hoje)
  alzTransfer?: number;
  totalLines?: number;
  counters?: Record<string, number>;
  files?: number;
}
export const getMonitorState = () => apiGet<MonitorState>('/admin/monitor/state');

// --- Status systemd dos servicos Cabal (canais + agents) ----------------

export interface ServiceRow {
  name: string;
  active: string;          // active|inactive|failed|activating
  sub: string;             // running|exited|dead|...
  pid?: number | null;
  since?: string | null;
  isChannel: boolean;
  channelNum?: number | null;
}

export interface ServicesStatus {
  total: number;
  up: number;
  down: number;
  channelsUp: number;
  channelsTotal: number;
  services: ServiceRow[];
}

export const getServicesStatus = () => apiGet<ServicesStatus>('/admin/monitor/services');

/* Reiniciar serviço/canal Cabal (systemctl restart) — SUPER-GM. Derruba players do canal. */
export const restartService = (name: string) =>
  apiPost<{ status: string; message: string; active: string }>(
    `/admin/monitor/services/${encodeURIComponent(name)}/restart`, {});

/* Reiniciar o SERVIDOR INTEIRO — lockout global de 60 s + `cabal-liberty restart` no host. SUPER-GM. */
export const restartServer = () =>
  apiPost<{ status: string; message: string; log: string }>('/admin/monitor/server/restart', {});

// --- Canal de guerra (canal 21) -------------------------------------------

export interface WarChannelStatus {
  isOnline: boolean;
  capella: number;
  procyon: number;
  other: number;
  total: number;
}

export const getWarChannelStatus = () => apiGet<WarChannelStatus>('/admin/monitor/warChannel');

export interface MonitorEvent {
  time: string;
  trc: string;
  label: string;
  cat: string;
  ch: string;
  d: string;
  fields?: [number, string][];  // campos crus do GameLog: [idx, valor] (so pos != \N)
  raw?: string;                 // linha crua completa do GameLog (pra ver hex total)
  catLabel?: string;            // label PT da categoria registrada (overlay do banco)
  catColor?: string;            // cor da categoria registrada (overlay do banco)
}

/* ─────────────────────── TRC (categorias + códigos) ──────────────────── */
export interface TrcCategory { id: number; name: string; label: string; color?: string | null; codeCount: number; }
export interface TrcCode {
  trc: number; categoryId?: number | null; categoryName?: string | null;
  eventName?: string | null; notes?: string | null; updatedAt?: string | null;
}
export interface TrcCodeList { total: number; items: TrcCode[]; }

export const listTrcCategories = () => apiGet<TrcCategory[]>('/admin/trc/categories');
export const createTrcCategory = (input: { name: string; label: string; color?: string }) =>
  apiPost<{ status: string; id: number }>('/admin/trc/categories', input);
export const updateTrcCategory = (id: number, input: { label?: string; color?: string }) =>
  apiPut<{ status: string }>(`/admin/trc/categories/${id}`, input);
export const listTrcCodes = (params: {
  categoryId?: number; search?: string; onlyUnnamed?: boolean; skip?: number; limit?: number;
}) => {
  const q: Record<string, string> = {};
  if (params.categoryId != null) q.categoryId = String(params.categoryId);
  if (params.search) q.search = params.search;
  if (params.onlyUnnamed) q.onlyUnnamed = 'true';
  if (params.skip != null) q.skip = String(params.skip);
  if (params.limit != null) q.limit = String(params.limit);
  return apiGet<TrcCodeList>('/admin/trc/codes', q);
};
export const upsertTrcCode = (input: {
  trc: number; categoryId?: number | null; eventName?: string | null; notes?: string | null;
}) => apiPost<{ status: string }>('/admin/trc/codes', input);
export const deleteTrcCode = (trc: number) =>
  apiDelete<{ status: string }>(`/admin/trc/codes/${trc}`);
export const getMonitorEvents = (params?: { limit?: number; cat?: string; q?: string }) => {
  const qs = new URLSearchParams();
  if (params?.limit) qs.append('limit', String(params.limit));
  if (params?.cat) qs.append('cat', params.cat);
  if (params?.q) qs.append('q', params.q);
  const tail = qs.toString() ? `?${qs.toString()}` : '';
  return apiGet<MonitorEvent[]>(`/admin/monitor/events${tail}`);
};
export const getMonitorHistory = () => apiGet<string[]>('/admin/monitor/history');
export const getMonitorDay = (day: string) => apiGet<any>(`/admin/monitor/day/${day}`);

/* ───────────────────────────── Payment Gateways (admin) ───────────────────── */

export type GatewayProvider = 'EFI' | 'MP' | 'PAYPAL';

export interface GatewaySummary {
  provider: GatewayProvider;
  enabled: boolean;
  displayName: string;
  ready: boolean;
  updatedAt?: string | null;
}

export interface EfiGateway {
  provider: 'EFI';
  enabled: boolean;
  displayName: string;
  sandbox: boolean;
  clientId: string;
  hasClientSecret: boolean;
  certificatePath: string;
  pixKey: string;
  webhookUrl: string;
  hasWebhookHmac: boolean;
  pixExpiration: number;
  updatedAt?: string | null;
  updatedBy?: number | null;
}

// Response do PUT /EFI: estende EfiGateway com info do auto-register de webhook.
// `webhookRegistered === null` significa que o backend nao tentou (URL nao mudou
// ou ficou vazia). True = registrado com sucesso, False = tentou e falhou.
export interface EfiGatewayPutResponse extends EfiGateway {
  webhookRegistered?: boolean | null;
  webhookRegisterError?: string | null;
}

export interface MpGateway {
  provider: 'MP';
  enabled: boolean;
  displayName: string;
  hasAccessToken: boolean;
  publicKey: string;
  hasWebhookSecret: boolean;
  acceptCreditCard: boolean;   // padrão false: preference exclui credit_card
  updatedAt?: string | null;
  updatedBy?: number | null;
}

export interface PaypalGateway {
  provider: 'PAYPAL';
  enabled: boolean;
  displayName: string;
  mode: 'Live' | 'Sandbox';
  clientId: string;
  hasClientSecret: boolean;
  updatedAt?: string | null;
  updatedBy?: number | null;
}

export interface EfiGatewayUpdate {
  enabled?: boolean;
  displayName?: string;
  sandbox?: boolean;
  clientId?: string;
  clientSecret?: string;
  certificatePath?: string;
  pixKey?: string;
  webhookUrl?: string;
  webhookHmac?: string;
  pixExpiration?: number;
}
export interface MpGatewayUpdate {
  enabled?: boolean;
  displayName?: string;
  accessToken?: string;
  publicKey?: string;
  webhookSecret?: string;
  acceptCreditCard?: boolean;
}
export interface PaypalGatewayUpdate {
  enabled?: boolean;
  displayName?: string;
  mode?: 'Live' | 'Sandbox';
  clientId?: string;
  clientSecret?: string;
}

export const listPaymentGateways = () =>
  apiGet<GatewaySummary[]>('/admin/payment-gateways');

export const getEfiGateway   = () => apiGet<EfiGateway>('/admin/payment-gateways/EFI');
export const getMpGateway    = () => apiGet<MpGateway>('/admin/payment-gateways/MP');
export const getPaypalGateway = () => apiGet<PaypalGateway>('/admin/payment-gateways/PAYPAL');

export const updateEfiGateway   = (input: EfiGatewayUpdate)   => apiPut<EfiGatewayPutResponse>('/admin/payment-gateways/EFI', input);
export const updateMpGateway    = (input: MpGatewayUpdate)    => apiPut<MpGateway>('/admin/payment-gateways/MP', input);
export const updatePaypalGateway = (input: PaypalGatewayUpdate) => apiPut<PaypalGateway>('/admin/payment-gateways/PAYPAL', input);

export const testEfiGateway = () =>
  apiPost<{ ok: boolean; error?: string; webhook?: unknown }>('/admin/payment-gateways/EFI/test', {});

/* ───────────────────────────── GM Access (admin) ──────────────────────────── */

export interface GmAccessRow {
  userNum: number;
  accountId?: string | null;
  gmLevel: number;
  grantedBy?: number | null;
  grantedById?: string | null;
  grantedAt?: string | null;
  notes?: string | null;
}

export interface AccountSearchRow {
  userNum: number;
  accountId: string;
  email?: string | null;
  hasGm: boolean;
  gmLevel?: number | null;
}

export interface GmAccessUpsert {
  gmLevel: number;
  notes?: string;
}

export const listGmAccess = () => apiGet<GmAccessRow[]>('/admin/access');
export const searchGmAccount = (q: string) =>
  apiGet<AccountSearchRow[]>(`/admin/access/search?q=${encodeURIComponent(q)}`);
export const upsertGmAccess = (userNum: number, input: GmAccessUpsert) =>
  apiPost<GmAccessRow>(`/admin/access/${userNum}`, input);
export const revokeGmAccess = (userNum: number) =>
  apiDelete<{ status: string; message: string }>(`/admin/access/${userNum}`);

/* ───────────────────────────── Lockout (admin) ────────────────────────────── */

export interface LockoutRow {
  id: number;
  targetUserNum?: number | null;
  reason: string;
  blockUntil: string;
  createdBy: number;
  createdAt: string;
}

export interface LockoutCreate {
  durationSeconds: number;
  reason: string;
  targetUserNum?: number | null;
}

export const listLockouts = () => apiGet<LockoutRow[]>('/admin/lockout');
export const createLockout = (input: LockoutCreate) =>
  apiPost<LockoutRow>('/admin/lockout', input);
export const cancelLockout = (id: number) =>
  apiDelete<{ status: string; message: string }>(`/admin/lockout/${id}`);

/* ───────────────────────────── GM Logs (admin) ────────────────────────────── */

export interface GmLogRow {
  id: number;
  actorUserNum: number;
  actorId?: string | null;
  action: string;
  targetUserNum?: number | null;
  targetId?: string | null;
  targetCharIdx?: number | null;
  details?: string | null;
  ipAddress?: string | null;
  createdAt: string;
}

export interface GmLogListResponse {
  total: number;
  items: GmLogRow[];
}

export interface GmLogFilter {
  skip?: number;
  limit?: number;
  action?: string;
  actor?: number;
  target?: number;
  dateFrom?: string;
  dateTo?: string;
}

export const listGmLogs = (params?: GmLogFilter) => {
  const qs = new URLSearchParams();
  if (params?.skip != null)  qs.append('skip', String(params.skip));
  if (params?.limit != null) qs.append('limit', String(params.limit));
  if (params?.action)        qs.append('action', params.action);
  if (params?.actor != null) qs.append('actor', String(params.actor));
  if (params?.target != null) qs.append('target', String(params.target));
  if (params?.dateFrom)      qs.append('dateFrom', params.dateFrom);
  if (params?.dateTo)        qs.append('dateTo', params.dateTo);
  const tail = qs.toString() ? `?${qs.toString()}` : '';
  return apiGet<GmLogListResponse>(`/admin/gm-logs${tail}`);
};

/* ───────────────────────────── Online Guard (admin) ───────────────────────── */

export interface GuardOnlineRow {
  charIdx?: number | null;
  charName?: string | null;
  userNum: number;
  accountId?: string | null;
  mapId?: number | null;
  mapName?: string | null;      // nome do mapa (o cliente ja sabe)
  classId?: number | null;
  nation?: number | null;
  state?: string | null;        // "Em Combate"/"BM3"/"AFK"/...
  channel?: number | null;
  hwid?: string | null;
  ip?: string | null;
  connected?: boolean;          // WS viva (substitui lastHeartbeat)
  dllHooked?: boolean;
  // legado (sempre null agora — posicao vive so no socket per-char):
  posX?: number | null;
  posY?: number | null;
  speed?: number | null;
  bikeSpeed?: number | null;
  alz?: number | null;
  status?: number;
  lastHeartbeatAt?: string | null;
}

// activeOnly=true → só conexões com personagem ativo (aba Onlines/dashboard).
// default (false) → todas as conexões, inclui quem só abriu o jogo (tela Guard).
export const listGuardOnline = (windowSec = 60, limit = 200, activeOnly = false) =>
  apiGet<GuardOnlineRow[]>(
    `/admin/guard/online?windowSec=${windowSec}&limit=${limit}${activeOnly ? '&activeOnly=true' : ''}`);

export const getCharacterGuardOnline = (charIdx: number) =>
  apiGet<GuardOnlineRow>(`/admin/guard/online/${charIdx}`);

/* ─── MoveGuard (anti-macro/bot): introspecção do detector contínuo ─── */
export interface MoveguardTop {
  charIdx: number;
  name?: string | null;
  suspect: number;   // score acumulado com decay (6h de meia-vida)
  hits: number;      // nº de janelas suspeitas na sessão
}
export interface MoveguardStats {
  posbatchHz: number;   // taxa real do POSBATCH que a .so empurra
  tracked: number;      // chars sob observação em memória
  calibrate: boolean;   // MOVEGUARD_CALIBRATE ligado?
  top: MoveguardTop[];  // top 25 por suspeita acumulada
}
export const getMoveguardStats = () =>
  apiGet<MoveguardStats>('/admin/guard/moveguard');

// Histórico PERSISTIDO de detecções (de _39_notifications, sobrevive a restart).
// Os números crus (ups/ratio/área…) vêm dentro de `message` já formatado.
export interface GuardAlert {
  notifId: number;
  kind: 'speed' | 'macro' | string;
  charIdx: number | null;
  name?: string | null;
  title?: string | null;
  message: string;
  priority?: number | null;
  ts?: string | null;         // ISO (CreatedAt)
}
export const listGuardAlerts = (kind?: 'speed' | 'macro', limit = 100) =>
  apiGet<GuardAlert[]>(`/admin/guard/alerts?limit=${limit}${kind ? `&kind=${kind}` : ''}`);

/* ─── Classificação de World (campo / dungeon / guerra) ─── */
// WorldIdx de mapas de CAMPO/CIDADE. Fora daqui = dungeon. World 15 = guerra.
// Espelha FIELD_WORLDS/WAR_WORLD do backend (app/admin/dashboard.py).
export const FIELD_WORLDS = new Set<number>([
  1, 2, 3, 4, 5, 6, 7, 8, 10, 11, 12, 14, 15, 19, 21, 22, 30,
  64, 66, 67, 69, 70, 71, 73, 159,
]);
export type WorldKind = 'campo' | 'dungeon' | 'guerra';
export function classifyWorld(worldId?: number | null): WorldKind {
  const w = worldId ?? -1;
  if (w === 15) return 'guerra';
  return FIELD_WORLDS.has(w) ? 'campo' : 'dungeon';
}

/* ───────────────────────────── Notificações (criar) ───────────────────────── */

export interface NotifCreate {
  kind: string;
  title?: string | null;
  message: string;
  priority?: number;
  targetCharIdx?: number | null;
  targetUserNum?: number | null;
  source?: string | null;
}

export const createAdminNotification = (body: NotifCreate) =>
  apiPost<{ notifId: number }>(`/admin/notifications/create`, body);

/* ───────────────────────────── Guard menu (full) ──────────────────────────── */

export interface GuardListRow {
  userNum: number;
  accountId?: string | null;
  hwid?: string | null;
  ip?: string | null;
  status: number;
  lastLogin?: string | null;
  lastHeartbeatAt?: string | null;
  heartbeatAgeSec?: number | null;
  connected?: boolean;            // DLL conectada agora (presenca WS)
  isOnline: boolean;
  onlineCharIdx?: number | null;
  onlineCharName?: string | null;
  onlineWorldIdx?: number | null;
  onlineChannelIdx?: number | null;
  mapId?: number | null;
  posX?: number | null;
  posY?: number | null;
  alz?: number | null;
  activeBans: number;
}

export interface GuardSummary {
  onlineInGame: number;
  onlineGuard: number;      // conectados via WebSocket
  totalBans: number;        // bans ativos
  bansToday: number;        // banimentos hoje
  distinctHwids: number;
}

export interface HwidAccount {
  userNum: number;
  accountId?: string | null;
  ip?: string | null;
  lastLogin?: string | null;
  lastHeartbeatAt?: string | null;
  isOnline: boolean;
  charName?: string | null;
  activeBans: number;
}

export interface HwidBanRow {
  banId: number;
  userNum: number;
  motivo: string;
  dataBanimento: string;
  dataLiberacao?: string | null;
  isActive: boolean;
}

export interface HwidDetail {
  hwid: string;
  lastSeenIp?: string | null;
  lastLogin?: string | null;
  lastHeartbeatAt?: string | null;
  accountsSameHwid: HwidAccount[];
  accountsSameIp: HwidAccount[];
  bansHistory: HwidBanRow[];
}

export const listGuard = (params: { limit?: number; search?: string } = {}) => {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);
  const s = qs.toString();
  return apiGet<GuardListRow[]>(`/admin/guard/list${s ? '?' + s : ''}`);
};

export const getGuardSummary = () => apiGet<GuardSummary>('/admin/guard/summary');

export const getHwidDetail = (hwid: string) =>
  apiGet<HwidDetail>(`/admin/guard/hwid/${encodeURIComponent(hwid)}`);

/** Resolve dados do Guard a partir do UserNum (HWID atual + histórico ban).
 *  Se a conta nunca teve heartbeat, devolve hwid=""; o consumer trata. */
export const getGuardByAccount = (userNum: number) =>
  apiGet<HwidDetail>(`/admin/guard/by-account/${userNum}`);

export interface GuardBanHistoryRow {
  banId: number;
  userNum: number;
  accountId?: string | null;
  motivo: string;
  dataBanimento: string;
  dataLiberacao?: string | null;
  isActive: boolean;
}

export const getGuardBansHistory = (limit = 200, onlyActive = false) =>
  apiGet<GuardBanHistoryRow[]>('/admin/guard/bans-history', {
    limit, onlyActive: String(onlyActive),
  });

export interface GuardBanInput {
  userNum: number;
  days: number;
  motivo: string;
}

export const guardBan = (input: GuardBanInput) =>
  apiPost<{ status: string; message: string; releaseDate: string }>('/admin/guard/ban', input);

export const guardUnban = (userNum: number) =>
  apiPost<{ status: string; message: string }>('/admin/guard/unban', { userNum });

export interface GuardForceRestartInput {
  targetUserNum?: number | null;
  durationSeconds?: number;
  reason?: string;
}

export const guardForceRestart = (input: GuardForceRestartInput) =>
  apiPost<{ status: string; lockoutId: number; blockUntil: string }>(
    '/admin/guard/force-restart',
    input,
  );

/* ─────────────────────────── Versão da DLL (Guard) ────────────────────────
 * Versão oficial da liberty.dll. A DLL manda a dela no WS (hello/verchk); o
 * hub compara e manda {"t":"update"} (MessageBox + fecha) em momento seguro
 * (abertura do jogo / troca de personagem). */

export interface DllVersionInfo {
  version: number;
  updatedAt?: string | null;
  updatedBy?: number | null;
}

export const getDllVersion = () => apiGet<DllVersionInfo>('/admin/guard/dll-version');

export const setDllVersion = (version: number) =>
  apiPut<{ status: string; version: number }>('/admin/guard/dll-version', { version });

/* ───────────────────────────── Crash Logs (guard) ─────────────────────────── */

export interface CrashLogRow {
  id: number;
  userNum?: number | null;
  accountId?: string | null;
  hwid?: string | null;
  dllVer?: number | null;
  ip?: string | null;
  crashedAt?: string | null;
  receivedAt?: string | null;
  resolved: number;
  content?: string | null;   // raw completo (mostrado ao expandir)
}

export const listCrashLogs = (limit = 200, onlyUnresolved = false) =>
  apiGet<CrashLogRow[]>('/admin/guard/crash-logs', {
    limit, onlyUnresolved: String(onlyUnresolved),
  });

export const resolveCrashLog = (id: number) =>
  apiPost<{ status: string; message: string }>(`/admin/guard/crash-logs/${id}/resolve`, {});

/* ───────────────────────────── Lookup (admin) ─────────────────────────────── */

export const lookupChars = (idxs: number[]) => {
  if (idxs.length === 0) return Promise.resolve({} as Record<number, string>);
  const qs = idxs.slice(0, 500).join(',');
  return apiGet<Record<number, string>>(`/admin/lookup/chars?idxs=${qs}`);
};

export const lookupAccounts = (nums: number[]) => {
  if (nums.length === 0) return Promise.resolve({} as Record<number, string>);
  const qs = nums.slice(0, 500).join(',');
  return apiGet<Record<number, string>>(`/admin/lookup/accounts?nums=${qs}`);
};

/* ───────────────────────────── Monitor Search (admin) ─────────────────────── */

export interface MonitorSearchParams {
  day?: string;             // YYYY-MM-DD
  cat?: string;             // csv de categorias (item,alz,...) ou 'all'
  trc?: string;             // csv de codes
  char?: number;
  user?: number;            // UserNum (traz todos os chars da conta)
  svc?: string;             // csv de serviços/canais (WorldSvr_01_02,LoginSvr_01)
  q?: string;
  skip?: number;
  limit?: number;
}

export const monitorSearch = (params: MonitorSearchParams) => {
  const qs = new URLSearchParams();
  if (params.day)  qs.append('day', params.day);
  if (params.cat)  qs.append('cat', params.cat);
  if (params.trc)  qs.append('trc', params.trc);
  if (params.char != null) qs.append('char', String(params.char));
  if (params.user != null) qs.append('user', String(params.user));
  if (params.svc)  qs.append('svc', params.svc);
  if (params.q)    qs.append('q', params.q);
  if (params.skip != null)  qs.append('skip', String(params.skip));
  if (params.limit != null) qs.append('limit', String(params.limit));
  return apiGet<MonitorEvent[]>(`/admin/monitor/search?${qs.toString()}`);
};

// Lista os serviços/canais (prefixos de .GameLog) disponiveis no dia.
export const getMonitorLogfiles = (day?: string) =>
  apiGet<string[]>(`/admin/monitor/logfiles${day ? `?day=${encodeURIComponent(day)}` : ''}`);

/* ───────────────────────────── Client Download (admin) ────────────────────── */

export interface ClientDownloadAdmin {
  url: string;
  label: string;
  version: string;
  sizeMb?: number | null;
  notes?: string | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
}

export interface ClientDownloadUpdate {
  url?: string;
  label?: string;
  version?: string;
  sizeMb?: number | null;
  notes?: string | null;
}

export const getClientDownloadAdmin = () =>
  apiGet<ClientDownloadAdmin>('/admin/client-download');

export const updateClientDownload = (input: ClientDownloadUpdate) =>
  apiPut<ClientDownloadAdmin>('/admin/client-download', input);

/** POST /admin/launcher/update — roda o update_all.sh (regenera manifests do launcher liberty-files). */
export const launcherUpdate = () =>
  apiPost<{ ok: boolean; log: string }>('/admin/launcher/update', {});

/* ───────────────────────────── Streamers (GM) ──────────────────────────────
 * Painel GM da plataforma de Streamers Oficiais. Todos os endpoints exigem
 * require_gm(3) no backend. JSON em camelCase. */

export type StreamerStatus = 'pending' | 'active' | 'inactive' | 'suspended';
export type StreamerPlatform = 'twitch' | 'youtube' | 'kick' | 'tiktok';
export type CommissionStatus = 'pendente' | 'pago' | 'cancelado';

export interface StreamerRow {
  id: number;
  userNum: number;
  accountId?: string | null;
  nick?: string | null;
  status: StreamerStatus;
  avatar?: string | null;
  commissionPct: number;
  createdAt?: string | null;
  approvedAt?: string | null;
  notes?: string | null;
  [key: string]: unknown;
}

export interface StreamerSocial {
  id?: number;
  network?: string | null;
  url: string;
}

export interface StreamerConnection {
  id?: number;
  platform: StreamerPlatform;
  channelId?: string | null;
  channelUrl?: string | null;
  verified?: boolean;
}

export interface StreamerCoupon {
  id?: number;
  code: string;
  kind?: string | null;
  discountPct?: number | null;
  expiresAt?: string | null;
  active?: boolean;
}

export interface StreamerCommission {
  id: number;
  streamerId: number;
  nick?: string | null;
  accountId?: string | null;
  brl: number;
  commissionBrl: number;
  commissionStatus: CommissionStatus;
  createdAt?: string | null;
  couponId?: number | null;
}

export interface StreamerSession {
  id: number;
  streamerId: number;
  nick?: string | null;
  platform?: StreamerPlatform | string | null;
  startAt?: string | null;
  endAt?: string | null;
  durationSec?: number | null;
  validated: boolean;
  spendBrl?: number | null;
  status?: string | null;
}

export interface StreamerDetail {
  streamer: StreamerRow;
  socials: StreamerSocial[];
  connections: StreamerConnection[];
  activeCoupon?: StreamerCoupon | null;
  recentCommissions: StreamerCommission[];
  recentSessions: StreamerSession[];
}

export interface StreamerLive {
  live: boolean;
  viewers?: number | null;
  title?: string | null;
  thumbnail?: string | null;
  videoId?: string | null;
  source?: string | null;
  connection?: {
    platform: StreamerPlatform | string;
    channelId?: string | null;
    channelUrl?: string | null;
  } | null;
}

export interface StreamerCredential {
  platform: StreamerPlatform;
  clientId?: string | null;
  hasClientSecret: boolean;
  hasApiKey: boolean;
  enabled: boolean;
  updatedAt?: string | null;
}

export interface StreamerCommissionsResponse {
  items: StreamerCommission[];
  total: number;
  totals: { paid: number; pending: number; cancelled: number };
}

export interface StreamerSessionsResponse {
  items: StreamerSession[];
  total: number;
}

export interface StreamerCouponRow {
  id: number;
  streamerId: number;
  streamerNick?: string | null;
  accountId?: string | null;
  code: string;
  type?: string | null;         // 'fixo' | 'live'
  cashbackPct?: number | null;
  active: boolean;              // Active=1 E nao expirado
  rawActive: boolean;
  expired: boolean;
  createdAt?: string | null;
  expiresAt?: string | null;
  sessionId?: number | null;
  usesCount: number;
}

export interface StreamerCouponsResponse {
  items: StreamerCouponRow[];
  total: number;
}

/* Streamers list / CRUD */

export const listStreamers = () =>
  apiGet<StreamerRow[]>('/admin/streamers');

export const createStreamer = (body: { userNum: number; nick?: string; commissionPct?: number }) =>
  apiPost<StreamerRow>('/admin/streamers', body);

export const updateStreamer = (
  id: number,
  patch: { status?: StreamerStatus; nick?: string; commissionPct?: number; notes?: string },
) => apiPatch<StreamerRow>(`/admin/streamers/${id}`, patch);

export const deleteStreamer = (id: number) =>
  apiDelete<{ status: string; message?: string }>(`/admin/streamers/${id}`);

export const getStreamerDetail = (id: number) =>
  apiGet<StreamerDetail>(`/admin/streamers/${id}`);

/* Commissions */

export const listStreamerCommissions = (params?: {
  status?: CommissionStatus;
  streamerId?: number;
  skip?: number;
  limit?: number;
}) => {
  const qs = new URLSearchParams();
  if (params?.status) qs.append('status', params.status);
  if (params?.streamerId != null) qs.append('streamerId', String(params.streamerId));
  if (params?.skip != null) qs.append('skip', String(params.skip));
  if (params?.limit != null) qs.append('limit', String(params.limit));
  const q = qs.toString();
  return apiGet<StreamerCommissionsResponse>(`/admin/streamer-commissions${q ? '?' + q : ''}`);
};

export const payStreamerCommission = (useId: number) =>
  apiPost<{ status: string; message?: string }>(`/admin/streamer-commissions/${useId}/pagar`, {});

/* Sessions */

export const listStreamerSessions = (params?: {
  streamerId?: number;
  skip?: number;
  limit?: number;
}) => {
  const qs = new URLSearchParams();
  if (params?.streamerId != null) qs.append('streamerId', String(params.streamerId));
  if (params?.skip != null) qs.append('skip', String(params.skip));
  if (params?.limit != null) qs.append('limit', String(params.limit));
  const q = qs.toString();
  return apiGet<StreamerSessionsResponse>(`/admin/streamer-sessions${q ? '?' + q : ''}`);
};

/* Coupons (todos os streamers) */

export const listStreamerCoupons = (params?: {
  type?: 'fixo' | 'live';
  streamerId?: number;
  onlyActive?: boolean;
  skip?: number;
  limit?: number;
}) => {
  const qs = new URLSearchParams();
  if (params?.type) qs.append('type', params.type);
  if (params?.streamerId != null) qs.append('streamerId', String(params.streamerId));
  if (params?.onlyActive) qs.append('onlyActive', 'true');
  if (params?.skip != null) qs.append('skip', String(params.skip));
  if (params?.limit != null) qs.append('limit', String(params.limit));
  const q = qs.toString();
  return apiGet<StreamerCouponsResponse>(`/admin/streamer-coupons${q ? '?' + q : ''}`);
};

/* Live status */

export const getStreamerLive = (id: number) =>
  apiGet<StreamerLive>(`/admin/streamer-live/${id}`);

/* Platform credentials */

export const listStreamerCredentials = () =>
  apiGet<StreamerCredential[]>('/admin/streamer-credentials');

export const updateStreamerCredential = (
  platform: StreamerPlatform,
  body: { clientId?: string; clientSecret?: string; apiKey?: string; enabled?: boolean },
) => apiPut<StreamerCredential>(`/admin/streamer-credentials/${platform}`, body);

/* ───────────────────────────── Cash Shop do site (admin CRUD) ─────────────── */

export interface ShopCategoryAdmin {
  id: number;
  name?: string;
  ordem?: number;
  products?: number;
}

export interface ShopCategoryAdminInput {
  name: string;
  ordem: number;
}

export interface ShopProductAdmin {
  id: number;
  categoryId: number;
  itemId: number;
  itemOpt: number;
  durationId: number;
  price: number;
  priceBonus?: number;
  image?: string | null;
  description?: string | null;
  title?: string | null;
  active?: number;
  itemCount?: number;
  accountBind?: number;
  optionsCount?: number;
}

export interface ShopProductAdminInput {
  categoryId: number;
  itemId: number;
  itemOpt: number;
  durationId: number;
  price: number;
  priceBonus: number;
  image?: string | null;
  description?: string | null;
  title?: string | null;
  active: number;
  itemCount: number;
  accountBind: number;
}

export interface ShopOptionAdmin {
  id: number;
  itemId: number;
  itemIdOpt: number;
  itemOpt: number;
  durationId: number;
  title?: string | null;
  description?: string | null;
  active?: number;
}

export interface ShopOptionAdminInput {
  itemIdOpt: number;
  itemOpt: number;
  durationId: number;
  title?: string | null;
  description?: string | null;
  active: number;
}

export const listShopCategoriesAdmin = () => apiGet<ShopCategoryAdmin[]>('/admin/shop/categories');
export const createShopCategory = (b: ShopCategoryAdminInput) => apiPost<ShopCategoryAdmin>('/admin/shop/categories', b);
export const updateShopCategory = (id: number, b: ShopCategoryAdminInput) => apiPut<ShopCategoryAdmin>(`/admin/shop/categories/${id}`, b);
export const deleteShopCategory = (id: number) => apiDelete<{ status: string; message: string }>(`/admin/shop/categories/${id}`);

export const listShopProductsAdmin = (categoryId?: number) =>
  apiGet<ShopProductAdmin[]>(`/admin/shop/products${categoryId != null ? `?categoryId=${categoryId}` : ''}`);
export const createShopProduct = (b: ShopProductAdminInput) => apiPost<ShopProductAdmin>('/admin/shop/products', b);
export const updateShopProduct = (id: number, b: ShopProductAdminInput) => apiPut<ShopProductAdmin>(`/admin/shop/products/${id}`, b);
export const deleteShopProduct = (id: number) => apiDelete<{ status: string; message: string }>(`/admin/shop/products/${id}`);

export const listShopOptionsAdmin = (productId: number) => apiGet<ShopOptionAdmin[]>(`/admin/shop/products/${productId}/options`);
export const createShopOption = (productId: number, b: ShopOptionAdminInput) => apiPost<ShopOptionAdmin>(`/admin/shop/products/${productId}/options`, b);
export const updateShopOption = (id: number, b: ShopOptionAdminInput) => apiPut<ShopOptionAdmin>(`/admin/shop/options/${id}`, b);
export const deleteShopOption = (id: number) => apiDelete<{ status: string; message: string }>(`/admin/shop/options/${id}`);

/** POST /admin/shop/upload — multipart; salva na pasta servida em /shop-img/ e retorna o nome do arquivo. */
export async function uploadShopImage(file: File): Promise<{ filename: string; url: string }> {
  const raw = localStorage.getItem('liberty_auth');
  let token = '';
  try { token = raw ? JSON.parse(raw).token : ''; } catch { /* ignore */ }
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/admin/shop/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Erro no upload' }));
    throw err;
  }
  return res.json();
}

// ── Bots (BotSvr) — GM4 (feature bots_manage) ──────────────────────────────
export interface BotAccountRow {
  userNum: number;
  accountId: string;
  characterIdx: number | null;
  name: string | null;
  level: number | null;
  charClass: number | null;
  nation: number | null;
  combatPower: number;
  online: boolean;
  worldIdx: number | null;
  x: number | null;
  y: number | null;
}

export interface OnlineBotRow {
  characterIdx: number;
  name: string;
  channel: number;
  mapId: number;
  x: number;
  y: number;
  state: number;
  uptime: number;
  source: 'v2';
  /** sussurros recebidos ainda não vistos no painel */
  unread: number;
}

export interface BotWhisperRow {
  id: number;
  ts: number;            // unix (s)
  dir: 'in' | 'out';     // in = recebido pelo bot | out = enviado pela staff
  name: string;          // quem mandou (in) / destino (out)
  text: string;
}

export const listBots = () => apiGet<BotAccountRow[]>('/admin/bots');
export const listOnlineBots = () => apiGet<OnlineBotRow[]>('/admin/bots/online');
export const startBots = (body: { channels: number[]; perChannel: number; delaySec: number }) =>
  apiPost<{ status: string; message: string; count: number }>('/admin/bots/start', body);
export const stopBots = () => apiPost<{ status: string; message: string }>('/admin/bots/stop', {});
/** também marca os recebidos como lidos */
export const listBotWhispers = (characterIdx: number) =>
  apiGet<BotWhisperRow[]>(`/admin/bots/${characterIdx}/whispers`);
export const sendBotWhisper = (characterIdx: number, body: { to: string; text: string }) =>
  apiPost<BotWhisperRow>(`/admin/bots/${characterIdx}/whisper`, body);
export const moveBots = (body: { characterIdxs: number[]; x: number; y: number }) =>
  apiPost<{ status: string; message: string; count: number }>('/admin/bots/move', body);
export const teleportBots = (body: { characterIdxs: number[]; world: number; x: number; y: number }) =>
  apiPost<{ status: string; message: string; count: number }>('/admin/bots/teleport', body);
